import { flexRender, type SortDirection } from '@tanstack/react-table';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  type JSX,
  memo,
  type PropsWithChildren,
  useCallback,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { type IntlShape, useIntl } from 'react-intl';

import * as alignmentStyles from './Alignment.sty.js';
import { DataTablePinningShadowHeaderCells } from './ColumnPinning/DataTablePinningShadowHeaderCells.js';
import * as headerCellStyles from './DataTableHeaderCell.sty.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import { TextEllipsis } from '../../../typography/text-ellipsis/TextEllipsis.js';
import { getDataTableBorderWidth } from '../../utils/data-table-border-width.js';
import { getColumnLabel } from '../../utils/get-column-label.js';
import {
  DATA_TABLE_DEFAULT_HORIZONTAL_ALIGNMENT,
  DATA_TABLE_SPACER_COLUMN_ID,
} from '../constants.js';
import {
  DataTableColumnContextProvider,
  DataTableHeaderContextProvider,
  useDataTable,
} from '../contexts/DataTableContext.js';
import * as tableStyles from '../DataTable.sty.js';
import * as columnOrderStyles from '../features/ColumnOrder/ColumnOrder.sty.js';
import { SortingArrowIndicators } from '../features/Sorting/SortingArrowIndicators.js';
import { SubRowsHeaderExpander } from '../features/SubRows/SubRowExpanders.js';
import { SubRowsHeaderWidthCorrection } from '../features/SubRows/SubRows.js';
import { InlineUserActionsMenu } from '../features/UserActions/InlineUserActionsMenu.js';
import { useHeaderCellInteractions } from '../hooks/useHeaderCellInteractions.js';
import { useHeaderCellDragAndDrop } from '../hooks/useHeaderDragAndDrop.js';
import type { Header } from '../hooks/useTable/types.js';
import { dataTableMessages } from '../messages.js';

interface DataTableHeaderCellProps extends PropsWithChildren {
  /**
   * Header to be rendered with this header cell.
   */
  header: Header<unknown, unknown>;

  /**
   * All headers in the same group (row).
   */
  headers: Header<unknown, unknown>[];

  /**
   * Header group id from the TanStack/table header object.
   */
  headerGroupId: string;

  /**
   * RowIndex based on the virtualization index.
   */
  rowIndex: number;

  /**
   * ColumnIndex based on the virtualization index.
   */
  columnIndex: number;

  /**
   * The index of the last header row.
   */
  lastRowIndex: number;
}

/**
 * Memoized wrapper for TextEllipsis in header cells to avoid unnecessary re-calculations.
 */
const HeaderTextEllipsis = memo(
  ({
    children,
    onTextOverflow,
  }: {
    children: string;
    onTextOverflow: (ellipsized: boolean) => void;
  }) => {
    return (
      <TextEllipsis
        onTextOverflow={onTextOverflow}
        className={headerCellStyles.headerCellEllipsis}
      >
        {children}
      </TextEllipsis>
    );
  },
);

/**
 * Hook that gets the aria-label for the header according to the different table configurations.
 *
 * @param param0 - object containing everything to create the labels
 * according to the current table configuration.
 * @returns the aria label for the header cell.
 * @internal
 */
export function getHeaderAriaLabel({
  intl,
  columnLabel,
  nextSortingOrder,
  canSort,
  columnDragAndDropMode,
  isCustomHeader,
  role,
}: {
  intl: IntlShape;
  columnLabel: string;
  nextSortingOrder: false | SortDirection | undefined;
  canSort?: boolean;
  columnDragAndDropMode: 'none' | 'enabled' | 'only';
  isCustomHeader: boolean;
  role: 'columnheader' | 'presentation';
}): string | undefined {
  // Don't set an aria-label on elements with role="presentation"
  if (role === 'presentation') {
    return undefined;
  }

  if (canSort && columnDragAndDropMode === 'none') {
    return intl.formatMessage(dataTableMessages.sortingLabel, {
      column: columnLabel,
      direction: nextSortingOrder,
    });
  }
  if (canSort && columnDragAndDropMode === 'enabled') {
    return intl.formatMessage(dataTableMessages.sortingDragAndDropLabel, {
      column: columnLabel,
      direction: nextSortingOrder,
    });
  }
  if (columnDragAndDropMode !== 'none') {
    return intl.formatMessage(dataTableMessages.dragAndDropLabel, {
      column: columnLabel,
    });
  }
  return isCustomHeader ? columnLabel : undefined;
}

/**
 * Gets the aria-description for the header according to the different sorting states.
 *
 * @returns the aria description for the header cell, or undefined if no description should be set.
 * @internal
 */
function getHeaderAriaSortDescription({
  intl,
  role,
  isSorted,
  multiSortPriority,
  sortedColumnsCount,
}: {
  intl: IntlShape;
  role: 'columnheader' | 'presentation';
  isSorted: false | SortDirection;
  multiSortPriority: number | undefined;
  sortedColumnsCount: number;
}): string | undefined {
  if (role === 'presentation' || !isSorted) {
    return undefined;
  }

  if (multiSortPriority !== undefined) {
    return intl.formatMessage(dataTableMessages.sortingStateWithPriorityLabel, {
      direction: isSorted,
      priority: multiSortPriority,
      total: sortedColumnsCount,
    });
  }

  return intl.formatMessage(dataTableMessages.sortingStateLabel, {
    direction: isSorted,
  });
}

/**
 * Returns the `data-dt-name` value for a header cell based on which interactions are enabled.
 *
 * @param param0 - object containing the necessary information to determine the correct `data-dt-name` value.
 * @returns the `data-dt-name` value for the header cell, or undefined if no interaction is enabled.
 * @internal
 */
function getInteractionDtName({
  columnDragAndDropMode,
  canSort,
}: {
  columnDragAndDropMode: 'none' | 'enabled' | 'only';
  canSort: boolean;
}): string | undefined {
  // only ordering is enabled, but not sorting
  if (
    columnDragAndDropMode === 'only' ||
    (columnDragAndDropMode === 'enabled' && !canSort)
  ) {
    return 'ColumnHeaderOrder';
  }
  // sorting and ordering are enabled together
  if (canSort && columnDragAndDropMode === 'enabled') {
    return 'ColumnHeaderOrderAndSort';
  }
  // only sorting is enabled, but not ordering
  return 'ColumnHeaderSort';
}

/**
 * Returns the `data-dt-properties` value for a header cell, reflecting the next sorting action
 * that will be performed when the user clicks the header.
 *
 * @param nextSortingOrder - the next sorting order.
 * @returns the `data-dt-properties` value, or undefined if sorting is not applicable.
 * @internal
 */
function getInteractionDtProperties(
  canSort: boolean,
  nextSortingOrder: false | SortDirection | undefined,
): string | undefined {
  if (!canSort || nextSortingOrder === undefined) {
    return undefined;
  }

  switch (nextSortingOrder) {
    case 'asc':
      return 'state:ascending';
    case 'desc':
      return 'state:descending';
    case false:
      return 'state:clearSort';
  }
}

/**
 * Returns the additional z-index elevation for a left-pinned column so that its
 * resize handle renders above the next pinned column to the right.
 * Returns 0 for non-left-pinned columns and parent headers without resize handles.
 * @internal
 */
function getLeftAdditionalElevation(
  isPinnedLeft: boolean,
  canResize: boolean,
  columnId: string,
  leftPinnedColumns: { id: string }[],
): string {
  if (!isPinnedLeft || !canResize) {
    return '0';
  }
  const idx = leftPinnedColumns.findIndex((c) => c.id === columnId);
  return `${leftPinnedColumns.length - 1 - idx}`;
}

export function DataTableHeaderCell(
  props: Readonly<DataTableHeaderCellProps>,
): JSX.Element | null {
  const {
    header,
    headers,
    headerGroupId,
    rowIndex,
    columnIndex: virtualColumnIndex,
    lastRowIndex,
  } = props;

  const intl = useIntl();
  const table = useDataTable();
  const { rowDensity, verticalDividers } = table.getVisualVariantOptions();
  const columnDragAndDropMode = header.getColumnDragAndDropMode();

  const gridPosition = header.getGridPosition(headers);
  const column = header.column;
  const { id: columnId, parent, columns: childColumns, columnDef } = column;
  const columnIndex =
    rowIndex === 0 ? gridPosition.gridColumnStart - 1 : virtualColumnIndex;

  const canSort = column.getCanSort();
  const canColumnActions = column.getCanColumnActions();

  const textOverflowRef = useRef(false);
  const handleTextOverflow = useCallback((ellipsized: boolean) => {
    textOverflowRef.current = ellipsized;
  }, []);

  const { setNodeRef, isDragging, portalRef, dragAndDropProps } =
    useHeaderCellDragAndDrop({
      headerId: header.id,
      column,
      columnIndex,
      columnDragAndDropMode,
    });
  const divRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs<HTMLElement | null>([setNodeRef, divRef]);

  const {
    handleCellMouseEnter,
    handleCellMouseLeave,
    handleCellActionsClose,
    handleCellClick,
  } = useHeaderCellInteractions({
    headerCell: divRef.current,
    canSort,
    canColumnActions,
    rowDensity,
    getToggleSortingHandler: column.getToggleSortingHandler,
  });

  // Early exit if the header is a placeholder to not render any content.
  if (header.isPlaceholder) {
    return null;
  }
  const headerGroupsLength = table.getHeaderGroups().length;
  const tableState = table.getState();
  // Only count columns that are actually sortable to avoid gaps in priority numbers
  // when a column is in the sorting state but has sorting disabled.
  const activeSortEntries =
    tableState.sorting?.filter((sort) => {
      const col = table.getColumn(sort.id);
      return col?.getCanSort() ?? false;
    }) ?? [];
  const sortedColumnsCount = activeSortEntries.length;

  const enableExpanding = table.options.enableExpanding;
  const canResize = column.getCanResize();
  const isResizing = column.getIsResizing();
  const isSorted = column.getIsSorted();
  const columnSortIndex = activeSortEntries.findIndex(
    (sort) => sort.id === column.id,
  );
  const multiSortPriority =
    sortedColumnsCount > 1 && columnSortIndex >= 0
      ? columnSortIndex + 1
      : undefined;
  const isColumnDragActive = tableState.draggedHeaderColumn !== undefined;

  const isRightAligned = columnDef.alignment === 'right';
  // even if vertical dividers are not set, they should be rendered for nested headers and when resizing is enabled
  const renderVerticalDividers =
    verticalDividers ||
    table.getHeaderGroups().length > 1 ||
    (table.options.enableColumnResizing &&
      (canResize ||
        // when resizing is enabled we need to render the vertical dividers for the spacer column as well
        column.id === DATA_TABLE_SPACER_COLUMN_ID));

  const floatingMenuHeaderColumnId =
    (tableState.floatingMenuInfo?.rowId === null &&
      tableState.floatingMenuInfo?.headerId) ||
    undefined;

  const hasCustomHeaderRenderer = columnDef.meta?.hasCustomHeaderRenderer;
  const children = hasCustomHeaderRenderer ? (
    <DataTableColumnContextProvider value={header.column}>
      {flexRender(columnDef.header, header.getContext())}
    </DataTableColumnContextProvider>
  ) : (
    flexRender(columnDef.header, header.getContext())
  );
  const isStringHeader = typeof children === 'string';

  const role =
    columnDef.meta?.spacer === true ? 'presentation' : 'columnheader';
  const columnLabel = getColumnLabel(column);
  const nextSortingOrder = column.getNextSortingOrder();
  const ariaLabel = getHeaderAriaLabel({
    intl,
    columnLabel,
    nextSortingOrder,
    canSort,
    columnDragAndDropMode,
    isCustomHeader: !isStringHeader,
    role,
  });
  const ariaSortDescription = getHeaderAriaSortDescription({
    intl,
    role,
    isSorted,
    multiSortPriority,
    sortedColumnsCount,
  });

  // subrows
  const isSubRowIndicatorColumn =
    enableExpanding === true &&
    table.getSubRowIndicatorColumnId() === column.id;
  const subRowsWidthCorrection =
    // only render the subrow width correction in the subrow indicator column
    enableExpanding === true && isSubRowIndicatorColumn ? (
      <SubRowsHeaderWidthCorrection
        subRowOpenDepth={table.getSubRowOpenDepth()}
      />
    ) : null;

  // column pinning
  const pinningPosition = header.getIsPinned();
  const isPinnedLeft = pinningPosition === 'left';
  const isPinnedRight = pinningPosition === 'right';
  const leftPinnedPosition = isPinnedLeft
    ? header.getLeftPinnedPosition()
    : undefined;
  const leftPinnedPositionPx =
    leftPinnedPosition === undefined ? undefined : `${leftPinnedPosition}px`;

  // Calculate additional z-index elevation for left-pinned columns so that the resize handle
  // of each column is rendered above the following pinned column to the right.
  const leftAdditionalElevation = getLeftAdditionalElevation(
    isPinnedLeft,
    canResize,
    columnId,
    table.getLeftVisibleLeafColumns(),
  );

  const rightPinnedPosition = isPinnedRight
    ? header.getRightPinnedPosition()
    : undefined;
  const rightPinnedPositionPx =
    rightPinnedPosition === undefined ? undefined : `${rightPinnedPosition}px`;

  const hasChildren = childColumns.length > 0;
  const isEdgeColumnInGroup =
    parent?.getCenterVisibleLeafColumns().at(-1) === column ||
    parent?.getLeftVisibleLeafColumns().at(-1) === column ||
    parent?.getRightVisibleLeafColumns().at(0) === column;

  const isLastVisibleLeftPinnedHeader = isPinnedLeft
    ? header.getIsLastVisibleLeftPinnedHeader()
    : false;
  const isFirstVisibleRightPinnedHeader = isPinnedRight
    ? header.getIsFirstVisibleRightPinnedHeader()
    : false;
  const isFirstVisibleUnpinnedHeader =
    pinningPosition === false
      ? header.getIsFirstVisibleUnpinnedHeader()
      : false;
  const isLastUnpinnedColumnWithoutSpacer =
    columnId === table.getCenterVisibleLeafColumns().at(-2)?.id;

  const edgeAdjustedGridPosition: {
    gridColumnStart: number;
    gridColumnEnd: number | `span ${number}`;
    gridRowStart: number;
    gridRowEnd: number | `span ${number}`;
  } = {
    gridColumnStart: gridPosition.gridColumnStart,
    gridColumnEnd: gridPosition.gridColumnEnd,
    gridRowStart: isEdgeColumnInGroup ? 1 : gridPosition.gridRowStart,
    gridRowEnd: isEdgeColumnInGroup
      ? `span ${headerGroupsLength}`
      : gridPosition.gridRowEnd,
  };

  const interactionDtName = getInteractionDtName({
    columnDragAndDropMode,
    canSort,
  });
  const interactionDtProperties = getInteractionDtProperties(
    canSort,
    nextSortingOrder,
  );

  /**
   * Only renders the subrow expander in the subrow indicator column
   * and if no custom header renderer is exposed.
   */
  const renderSubRowHeaderExpander = (): JSX.Element | null => {
    if (
      !enableExpanding ||
      !isSubRowIndicatorColumn ||
      hasCustomHeaderRenderer
    ) {
      return null;
    }

    return (
      <SubRowsHeaderExpander isAllRowsExpanded={table.getIsAllRowsExpanded()} />
    );
  };

  let headerContent = (
    <div
      {...dragAndDropProps}
      key={`Header-${columnId}`}
      ref={mergedRef}
      role={role}
      data-headerid={header?.id}
      data-header-row={rowIndex}
      data-column={columnIndex}
      data-columnid={columnId}
      data-columntype={columnDef.columnType}
      data-dt-name={interactionDtName}
      data-dt-properties={interactionDtProperties}
      className={clsx(
        headerCellStyles.headerCell({
          rowDensity: hasCustomHeaderRenderer
            ? 'customHeaderRenderer'
            : rowDensity,
          pinnedColumn: pinningPosition,
          verticalDividers: renderVerticalDividers,
          isDragAndDropDisabled: header.getIsDragAndDropDisabled(),
        }),
        alignmentStyles.horizontalAlignment[
          columnDef.alignment ?? DATA_TABLE_DEFAULT_HORIZONTAL_ALIGNMENT
        ],
        alignmentStyles.verticalAlignment[table.getHeaderVerticalAlignment()],
        {
          [headerCellStyles.firstColumn]: columnIndex === 0,
          [headerCellStyles.firstHeaderRow]: rowIndex === 0,
          [headerCellStyles.lastLeftPinnedColumn]:
            isLastVisibleLeftPinnedHeader,
          [headerCellStyles.firstUnpinnedColumn]: isFirstVisibleUnpinnedHeader,
          [headerCellStyles.firstRightPinnedColumn]:
            isFirstVisibleRightPinnedHeader,
          [headerCellStyles.lastHeaderRow]: rowIndex === lastRowIndex,
          [headerCellStyles.parentHeader]: hasChildren,
          [headerCellStyles.childHeader]: !!parent,
          [headerCellStyles.sortableHeader]: canSort,
          [headerCellStyles.isSortedHeader]: isSorted,
          [headerCellStyles.isMultiSortedHeader]:
            multiSortPriority !== undefined,
          [tableStyles.prefixColumn]:
            columnDef.meta?.builtinPlacement === 'prefix',
          [headerCellStyles.headerCellActiveInteraction]:
            floatingMenuHeaderColumnId === header.id,
          [headerCellStyles.customHeaderDensity[rowDensity]]:
            hasCustomHeaderRenderer,
          [headerCellStyles.subRowIndicatorColumn]: isSubRowIndicatorColumn,
          // add grab cursor styling only if column drag and drop is enabled but sorting is not
          [columnOrderStyles.headerDragAndDrop.enabled]:
            !isDragging &&
            columnDragAndDropMode !== 'none' &&
            !header.getCanSortByHeaderClick(),
          [columnOrderStyles.headerDragAndDrop.inactive]:
            tableState.draggedHeaderColumn !== undefined,
        },
      )}
      aria-sort={header.getAriaSortAttribute()}
      aria-description={ariaSortDescription}
      data-sort-desc-first={columnDef.sortDescFirst ?? false}
      aria-label={ariaLabel}
      style={{
        ...assignInlineVars({
          [headerCellStyles.leftPositionVar]: leftPinnedPositionPx,
          [headerCellStyles.rightPositionVar]: rightPinnedPositionPx,
          [headerCellStyles.leftAdditionalElevationVar]:
            leftAdditionalElevation,
        }),
        ...gridPosition,
      }}
      onMouseEnter={handleCellMouseEnter}
      onMouseLeave={handleCellMouseLeave}
      onClick={handleCellClick}
    >
      {renderSubRowHeaderExpander()}

      {isRightAligned && subRowsWidthCorrection}

      {isStringHeader ? (
        <HeaderTextEllipsis onTextOverflow={handleTextOverflow}>
          {children}
        </HeaderTextEllipsis>
      ) : (
        children
      )}

      {canSort && (
        <SortingArrowIndicators
          isSorted={isSorted}
          nextSortingOrder={nextSortingOrder}
          multiSortPriority={multiSortPriority}
          rowDensity={rowDensity}
        />
      )}

      {canColumnActions && !isColumnDragActive && (
        <DataTableHeaderContextProvider value={header}>
          <InlineUserActionsMenu
            headerGroupOrRowId={headerGroupId}
            columnId={columnId}
            type="header"
            aria-label={intl.formatMessage(dataTableMessages.columnActionLabel)}
            data-header-row={rowIndex}
            data-column={columnIndex}
            onClose={handleCellActionsClose}
          />
        </DataTableHeaderContextProvider>
      )}

      {!isRightAligned && subRowsWidthCorrection}

      {canResize && !isColumnDragActive && (
        <button
          type="button"
          key={`Header-${columnId}-resize-handler`}
          aria-label={intl.formatMessage(dataTableMessages.resizeColumnLabel, {
            column: columnLabel,
          })}
          data-header-row={rowIndex}
          data-column={columnIndex}
          data-trigger="resize"
          className={clsx(
            headerCellStyles.columnSizingHandle({
              active: isResizing,
              last: isLastUnpinnedColumnWithoutSpacer,
              pinnedColumn: pinningPosition,
            }),
            {
              [headerCellStyles.firstRightPinnedColumn]:
                isFirstVisibleRightPinnedHeader,
            },
          )}
          {...(isEdgeColumnInGroup && {
            style: {
              // if this is the last column of a header group, the resize handle should stretch the full header height
              height: table.getTotalHeaderHeight() - getDataTableBorderWidth(),
            },
          })}
          onMouseDown={header.handleResize}
          onTouchStart={header.handleResize}
          onKeyUp={header.handleKeyboardResize}
          onDoubleClick={header.handleResizeExpand}
          data-dt-name="ColumnHeaderResize"
        />
      )}
    </div>
  );

  // when dragging, the header is portaled out and rendered into the drag overlay
  if (isDragging) {
    headerContent = createPortal(
      headerContent,
      portalRef.current || document.body,
    );
  }

  // for text headers a tooltip should be rendered when the text overflows
  if (isStringHeader) {
    headerContent = (
      <Tooltip disabled={!textOverflowRef.current} text={children}>
        {headerContent}
      </Tooltip>
    );
  }

  return (
    <>
      {headerContent}
      <DataTablePinningShadowHeaderCells
        isLeftShadow={isLastVisibleLeftPinnedHeader && !hasChildren}
        isRightShadow={isFirstVisibleRightPinnedHeader && !hasChildren}
        leftPosition={leftPinnedPosition}
        rightPosition={rightPinnedPosition}
        gridPosition={edgeAdjustedGridPosition}
      />
    </>
  );
}

DataTableHeaderCell.displayName = 'DataTableHeaderCell';
